import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import logoUrl from '../../src/styles/logo.png';
import { Users, Briefcase, Zap, LogOut, Settings, LayoutDashboard, Building2, ArrowRight } from 'lucide-react';
import { NotificationCenter } from '../notifications/NotificationCenter';
import { useAuth } from '../../context/AuthContext';
import { useOrgMembers } from '../../hooks/useOrgMembers';
import { useQuotas } from '../../hooks/useQuotas';
import { PLANS } from '../../lib/plans';
import { UPGRADE_MODAL_EVENT } from '../../lib/quotaErrors';
import UpgradeModal from './UpgradeModal';
import QuotaMeter from './QuotaMeter';

const menuItems = [
  { path: '/home', label: 'Tableau de bord', icon: <LayoutDashboard size={20} /> },
  { path: '/missions', label: 'Missions', icon: <Briefcase size={20} /> },
  { path: '/clients', label: 'Clients', icon: <Building2 size={20} /> },
  { path: '/talents', label: 'Talents', icon: <Users size={20} /> },
  { path: '/matching', label: 'Matching IA', icon: <Zap size={20} /> },
];

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const { members } = useOrgMembers();
  const { plan, items, loading: quotasLoading } = useQuotas();
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  useEffect(() => {
    onClose();
  }, [location.pathname]);

  // L'UpgradeModal n'est montée qu'ici : les interceptions 429 (useUpload,
  // MatchingContext) demandent son ouverture via cet événement.
  useEffect(() => {
    const open = () => setUpgradeOpen(true);
    window.addEventListener(UPGRADE_MODAL_EVENT, open);
    return () => window.removeEventListener(UPGRADE_MODAL_EVENT, open);
  }, []);

  const handleSignOut = async () => {
    // signOut redirige lui-même vers la landing publique.
    await signOut();
  };

  const currentMember = members.find(m => m.user_id === user?.id);
  const displayName = user?.user_metadata?.full_name ?? user?.email ?? '—';
  const userInitials = displayName.includes('@')
    ? displayName.slice(0, 2).toUpperCase()
    : displayName.split(' ').filter(Boolean).map((w: string) => w[0]).slice(0, 2).join('').toUpperCase();

  const isActive = (path: string) => {
    if (path === '/home') return location.pathname === '/home' || location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Overlay mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <div
        className={`
          w-64 bg-slate-900 text-slate-300 flex flex-col h-screen fixed left-0 top-0
          border-r border-slate-800 z-50 transition-transform duration-300
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0
        `}
      >
        {/* Brand */}
        <div
          onClick={() => navigate('/home')}
          className="h-16 flex items-center px-6 border-b border-slate-800 bg-slate-950 cursor-pointer group"
        >
          <div className="flex items-center gap-2 text-white group-hover:scale-105 transition-transform">
            <img src={logoUrl} alt="RevertAI Logo" className="h-8 w-auto object-contain" />
            <span className="font-bold text-lg tracking-tight text-white">Revert<span className="text-amber-500">AI</span></span>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-6 px-3 space-y-1">
          {menuItems.map((item) => (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`
                w-full flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium transition-all duration-200
                ${isActive(item.path)
                  ? 'bg-amber-600 text-white shadow-lg shadow-amber-900/20 translate-x-1'
                  : 'hover:bg-slate-800 hover:text-white hover:translate-x-1'
                }
              `}
            >
              {item.icon}
              {item.label}
            </button>
          ))}
        </nav>

        {/* Plan & quotas */}
        <div className="px-3 mb-2">
          <div className="p-3.5 rounded-xl bg-slate-800/60 border border-slate-700/60">
            <div className="flex items-center gap-2 mb-2.5">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-700 text-slate-300 text-[10px] font-bold uppercase tracking-wide">
                {PLANS[plan].label}
              </span>
              <span className="text-[10px] text-slate-500">Plan actuel</span>
            </div>
            {!quotasLoading && (
              <div className="space-y-2 mb-3">
                {items.map(item => (
                  <QuotaMeter key={item.key} label={item.label} used={item.used} limit={item.limit} tone="dark" />
                ))}
              </div>
            )}
            <button
              onClick={() => setUpgradeOpen(true)}
              className="w-full text-xs font-semibold bg-amber-600/90 hover:bg-amber-600 text-white py-2 rounded-lg transition-colors flex items-center justify-center gap-1.5"
            >
              Augmenter mes limites <ArrowRight size={12} />
            </button>
          </div>
        </div>

        {/* Bottom Actions */}
        <div className="p-4 border-t border-slate-800 bg-slate-950/50">
          <div className="flex items-center justify-between mb-4 px-2">
            <NotificationCenter />
            <button
              onClick={() => navigate('/settings')}
              className={`p-2 rounded-lg transition-colors ${isActive('/settings') ? 'text-amber-500 bg-slate-800' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}
              title="Paramètres"
            >
              <Settings size={20} />
            </button>
          </div>

          <div className="flex items-center gap-3 pt-4 border-t border-slate-800">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center text-white text-xs font-bold ring-2 ring-slate-800">
              {userInitials}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{displayName}</p>
              <p className="text-xs text-slate-500 truncate capitalize">{currentMember?.role ?? '—'}</p>
            </div>
            <button
              onClick={handleSignOut}
              className="text-slate-500 hover:text-amber-500 transition-colors"
              title="Se déconnecter"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Upgrade modal */}
      <UpgradeModal
        isOpen={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        currentPlan={plan}
      />
    </>
  );
};

export default Sidebar;
